/**
 * Derived tenant-journey stage layer.
 *
 * The redesigned tenant profile presents a single linear "journey" of 11
 * stages:
 *
 *   Applied → Signature pending → Deposit pending → Key pickup pending →
 *   Handover protocol pending → Active → Move-out scheduled →
 *   Move-out protocol pending → Key return pending → Deposit refund pending →
 *   Moved out
 *
 * `bookings.status` is a flat 12-value enum and cannot express all of these,
 * so this module DERIVES the current stage from the real signals we already
 * have (contract/deposit/key/protocol state) instead of storing a new status.
 * This keeps `bookings.status` — and every existing consumer of it — untouched
 * (see src/lib/leaseStatus.ts, the canonical pipeline resolver this layers on
 * top of).
 *
 * GOLDEN RULE — a stage is only reached when the conditions of EVERY earlier
 * stage are fulfilled too. The resolver walks the ladder in order and stops at
 * the first unmet gate (`JOURNEY_GATES`), so a tenant can never read "Active"
 * with no contract on file or no key handover, and `move_out_pending` can never
 * skip the move-in half. The only exception is a terminal status
 * (moved_out/terminated/cancelled): that's an EXIT, not an upgrade, so it always
 * lands on the closed stage. When `bookings.status` claims a stage the
 * conditions don't support, the derived stage stays at the real one and
 * `statusAhead` flags the discrepancy for the UI.
 *
 * EVIDENCE, NOT CLAIMS — the corollary. `bookings.status` and
 * `bookings.contract_status` are plain enum columns that any flow (or an admin)
 * can write; on their own they assert a stage rather than prove it. Every gate
 * that CAN be backed by an artefact is anded with that artefact:
 *   - contract stages require a contract document (`hasContractDocument`) —
 *     `contract_status = 'signed'` with nothing on file no longer counts;
 *   - the deposit gate takes the canonical `resolveDepositState(...) === "paid"`
 *     (the full contractual deposit), not "some deposit row says paid";
 *   - key pickup / return require their timestamps;
 *   - the protocol stages require a SUBMITTED handover protocol;
 *   - Active requires a submitted move-in protocol or `move_in_confirmed_at`
 *     (written by every path that establishes a move-in — see the gate);
 *   - "Move-out notice recorded" requires `move_out_notice_at`;
 *   - "Move-out date confirmed" requires `move_out_date`.
 * NO gate is satisfiable by a status value alone. The columns the last three
 * need were added (and backfilled, so the change is stage-neutral) by
 * 20260731100000_tenant_journey_evidence_columns.sql.
 *
 * Signals used, and where they live:
 *  - contract sent / signed      → bookings.contract_status / contract_signed_at
 *  - deposit paid                → payments rows (passed in as `depositPaid`)
 *  - key picked up / returned    → bookings.key_picked_up_at / key_returned_at
 *                                  (added by 20260718*_booking_key_handover_tracking)
 *  - move-in / move-out protocol → handover_protocols (passed in as booleans)
 *  - move-out scheduled/confirmed→ bookings.status notice_given / move_out_pending
 *  - moved out / terminal        → bookings.status moved_out|terminated|cancelled
 */

import { isBookingContractSigned } from "@/lib/leaseStatus";

export type TenantJourneyStageId =
  | "applied"
  | "signature_pending"
  | "deposit_pending"
  | "key_pickup_pending"
  | "handover_pending"
  | "active"
  | "moveout_scheduled"
  | "moveout_protocol_pending"
  | "key_return_pending"
  | "deposit_refund_pending"
  | "moved_out";

export interface TenantJourneyStageMeta {
  id: TenantJourneyStageId;
  label: string;
  /** status dot colour */
  dot: string;
  /** badge text colour */
  text: string;
  /** badge background */
  bg: string;
  /** badge border */
  border: string;
  /** dot ring (translucent) */
  ring: string;
}

/**
 * Stage metadata — colours mirror the handoff prototype's STATES array 1:1 so
 * the badge renders identically. Order is the canonical journey order.
 */
export const TENANT_JOURNEY_STAGES: readonly TenantJourneyStageMeta[] = [
  {
    id: "applied",
    label: "Applied",
    dot: "#64748B",
    text: "#475569",
    bg: "#EEF2F6",
    border: "#DDE3EA",
    ring: "rgba(100,116,139,.18)",
  },
  {
    id: "signature_pending",
    label: "Signature pending",
    dot: "#D97706",
    text: "#B45309",
    bg: "#FEF6E7",
    border: "#F7E4BD",
    ring: "rgba(217,119,6,.16)",
  },
  {
    id: "deposit_pending",
    label: "Deposit pending",
    dot: "#2563EB",
    text: "#1D4ED8",
    bg: "#EAF1FE",
    border: "#D3E1FD",
    ring: "rgba(37,99,235,.15)",
  },
  {
    id: "key_pickup_pending",
    label: "Key pickup pending",
    dot: "#0891B2",
    text: "#0E7490",
    bg: "#E5FAFD",
    border: "#C4EFF6",
    ring: "rgba(8,145,178,.15)",
  },
  {
    id: "handover_pending",
    label: "Move-in protocol pending",
    dot: "#7C3AED",
    text: "#6D28D9",
    bg: "#F1ECFE",
    border: "#E1D5FB",
    ring: "rgba(124,58,237,.15)",
  },
  {
    id: "active",
    label: "Active",
    dot: "#10B981",
    text: "#047857",
    bg: "#E6F7EF",
    border: "#C6EEDB",
    ring: "rgba(16,185,129,.18)",
  },
  {
    id: "moveout_scheduled",
    label: "Move-out scheduled",
    dot: "#EA580C",
    text: "#C2410C",
    bg: "#FFF1E8",
    border: "#FBD6BF",
    ring: "rgba(234,88,12,.16)",
  },
  {
    id: "moveout_protocol_pending",
    label: "Move-out protocol pending",
    dot: "#DB2777",
    text: "#BE185D",
    bg: "#FDECF4",
    border: "#F7D0E3",
    ring: "rgba(219,39,119,.15)",
  },
  {
    id: "key_return_pending",
    label: "Key return pending",
    dot: "#0891B2",
    text: "#0E7490",
    bg: "#E5FAFD",
    border: "#C4EFF6",
    ring: "rgba(8,145,178,.15)",
  },
  {
    id: "deposit_refund_pending",
    label: "Deposit refund pending",
    // Teal, matching the deposit surfaces elsewhere in the profile — the badge
    // should read as "money outstanding", not as another handover step.
    dot: "#0F5C52",
    text: "#0C4B43",
    bg: "#E7F5F1",
    border: "#C9E6DE",
    ring: "rgba(15,92,82,.16)",
  },
  {
    id: "moved_out",
    label: "Moved out",
    dot: "#475569",
    text: "#334155",
    bg: "#EDF1F5",
    border: "#DAE1E9",
    ring: "rgba(71,85,105,.16)",
  },
] as const;

const STAGE_INDEX: Record<TenantJourneyStageId, number> =
  TENANT_JOURNEY_STAGES.reduce(
    (acc, s, i) => {
      acc[s.id] = i;
      return acc;
    },
    {} as Record<TenantJourneyStageId, number>
  );

export function tenantJourneyStageMeta(
  id: TenantJourneyStageId
): TenantJourneyStageMeta {
  return TENANT_JOURNEY_STAGES[STAGE_INDEX[id]];
}

/**
 * Translated stage label. The stage meta's `label` is only the English
 * fallback (used for badge colour lookup and non-UI contexts like toasts) —
 * anywhere a stage name renders in the UI, go through this so it picks up
 * `admin.tenantJourney.stage.<id>` from `public.translations`.
 */
export function tenantJourneyStageLabel(
  t: (key: string, fallback?: string) => string,
  id: TenantJourneyStageId
): string {
  const meta = tenantJourneyStageMeta(id);
  return t(`admin.tenantJourney.stage.${id}`, meta.label);
}

/** Position of a stage on the ladder (0 = Applied … 10 = Moved out). */
export function tenantJourneyStageIndex(id: TenantJourneyStageId): number {
  return STAGE_INDEX[id];
}

/**
 * Current-resident stages: the tenant has moved in and has not yet handed the
 * keys back (Active through Key return pending). These count as "active" in
 * rollups.
 *
 * The upper bound is `deposit_refund_pending`, not `moved_out`: that stage means
 * the keys ARE back and only the deposit is outstanding, so counting it as a
 * resident would inflate occupancy with people who have left. Bounding on
 * `moved_out` would have done exactly that once a stage was inserted between the
 * two — the range was written when `key_return_pending` was the last non-terminal
 * stage, which made the two bounds equivalent.
 */
export function isResidentJourneyStage(id: TenantJourneyStageId): boolean {
  return (
    STAGE_INDEX[id] >= STAGE_INDEX.active &&
    STAGE_INDEX[id] < STAGE_INDEX.deposit_refund_pending
  );
}

/**
 * Pre-move-in pipeline stages: everything before Active (Applied through
 * Move-in protocol pending) — the tenant is still being onboarded.
 */
export function isPreMoveInJourneyStage(id: TenantJourneyStageId): boolean {
  return STAGE_INDEX[id] < STAGE_INDEX.active;
}

export interface TenantJourneySignals {
  /** deposit fully paid (from payments rows) */
  depositPaid: boolean;
  /** a move-in handover protocol exists and is submitted */
  moveInProtocolSubmitted: boolean;
  /** a move-out handover protocol exists and is submitted */
  moveOutProtocolSubmitted: boolean;
  /**
   * The deposit refund has been decided AND the money has actually moved
   * (deposit_settlements.refund_paid_at). Both halves matter: a released deposit
   * the tenant has not been paid is not a finished tenancy.
   *
   * Optional so every existing caller keeps compiling — absent reads as "not
   * settled", which parks a post-key-return tenancy at deposit_refund_pending
   * rather than silently promoting it past a deposit nobody has looked at.
   */
  depositRefundSettled?: boolean;
  /**
   * No deposit was ever held for this tenancy, so there is nothing to refund.
   * Distinct from "not settled yet": a zero-deposit tenancy must not sit forever
   * in a stage it can never leave.
   */
  noDepositHeld?: boolean;
}

export interface TenantJourney {
  stageId: TenantJourneyStageId;
  /** 0-based index into TENANT_JOURNEY_STAGES */
  stageIndex: number;
  stage: TenantJourneyStageMeta;
  /** "Step N of 10" */
  stepText: string;
  /** true once the deal is closed (moved out / terminal) */
  isClosed: boolean;
  /**
   * When closed, WHICH terminal the deal reached — so a `cancelled`/`terminated`
   * booking (which never became a tenant) can be presented distinctly from a
   * genuine move-out instead of both collapsing to the "Moved out" stage.
   * `moved_out` also covers reaching the moved-out stage via a recorded key
   * return without a closed status. `null` while still in an active stage.
   */
  terminalStatus: "moved_out" | "terminated" | "cancelled" | null;
  /** whether a given stage has been reached (>= current) */
  reached: (id: TenantJourneyStageId) => boolean;
  /** raw booking status for reference */
  bookingStatus: string;
  /**
   * The gate that stops the tenant from advancing — the condition of the NEXT
   * stage, which is not fulfilled yet. `null` at the closed stage (nothing left
   * to reach).
   */
  blockedBy: { stageId: TenantJourneyStageId; requirement: string } | null;
  /**
   * The stage `bookings.status` alone claims, when that status only ever exists
   * post-move-in (`active` / `notice_given` / `move_out_pending`). `null` for
   * in-flight and terminal statuses, which make no claim of their own.
   */
  claimedStageId: TenantJourneyStageId | null;
  /**
   * True when `bookings.status` claims a LATER stage than the conditions
   * support (e.g. status `active` on a booking with no signed contract). The
   * derived stage stays at the real one; surfacing this lets the UI explain the
   * mismatch instead of silently promoting the tenant.
   */
  statusAhead: boolean;
}

export const CLOSED_STATUSES = new Set([
  "moved_out",
  "terminated",
  "cancelled",
]);

/** Statuses that mean move-out notice has been recorded. */
export const NOTICE_GIVEN_STATUSES = new Set([
  "notice_given",
  "move_out_pending",
]);

/** The stage a bare `bookings.status` claims (see `claimedStageId`). */
const STATUS_CLAIMED_STAGE: Record<string, TenantJourneyStageId> = {
  active: "active",
  notice_given: "moveout_scheduled",
  move_out_pending: "moveout_protocol_pending",
};

export interface JourneyGate {
  id: TenantJourneyStageId;
  /** plain-language description of what must be true to ENTER this stage */
  requirement: string;
  met: (booking: any, signals: TenantJourneySignals, status: string) => boolean;
}

/**
 * The entry condition of each stage, in canonical journey order (same order as
 * TENANT_JOURNEY_STAGES — asserted in tenantJourney.test.ts). Each gate states
 * ONLY its own condition; the golden rule (all earlier gates must pass too) is
 * enforced by the sequential walk in `resolveTenantJourney`.
 */
export const JOURNEY_GATES: readonly JourneyGate[] = [
  {
    id: "applied",
    requirement: "Application received",
    met: () => true,
  },
  {
    id: "signature_pending",
    requirement: "Contract generated or uploaded",
    met: (b) => isContractSentForSignature(b),
  },
  {
    id: "deposit_pending",
    // Manual-inclusive on purpose: an admin-uploaded signed PDF counts as
    // signed even though it may not carry a DocuSign signature timestamp —
    // but the PDF has to actually be on file (isContractSignedForJourney).
    requirement: "Contract signed (signed copy on file)",
    met: (b) => isContractSignedForJourney(b),
  },
  {
    id: "key_pickup_pending",
    // `depositPaid` is the canonical resolveDepositState(...) === "paid" —
    // the FULL contractual deposit, not merely "some deposit row is paid".
    requirement: "Security deposit paid in full",
    met: (_b, s) => s.depositPaid,
  },
  {
    id: "handover_pending",
    requirement: "Key pickup recorded",
    met: (b) => !!b?.key_picked_up_at,
  },
  {
    id: "active",
    // Evidence, not status: a submitted move-in protocol, or a recorded
    // move-in confirmation. `status = active` on its own no longer opens this
    // gate.
    //
    // `move_in_confirmed_at` is the SINGLE column that carries the
    // confirmation, deliberately: every path that establishes a move-in writes
    // it (the handover_protocols trigger, "Mark move-in complete", the edit
    // panel's Actual move-in field, useCreateBooking, and the migration
    // backfill from `actual_move_in_date`). Reading `actual_move_in_date` here
    // as a second, equal source would force the force-stage patch to null that
    // column on a downgrade — and it also feeds the
    // Wohnungsgeberbestätigung send flow, so a stage correction would silently
    // destroy another feature's data.
    requirement: "Move-in protocol submitted or move-in confirmed",
    met: (b, s) =>
      s.moveInProtocolSubmitted || nonEmpty(b?.move_in_confirmed_at),
  },
  {
    id: "moveout_scheduled",
    // The notice timestamp is the evidence; the status records the decision.
    requirement: "Move-out notice recorded",
    met: (b, _s, status) =>
      NOTICE_GIVEN_STATUSES.has(status) && nonEmpty(b?.move_out_notice_at),
  },
  {
    id: "moveout_protocol_pending",
    // Both halves: the status records that the date was confirmed, and the
    // date itself must be on the booking. `status` alone is a claim — the
    // stage is literally named after the date, so the date is the evidence.
    requirement: "Move-out date confirmed",
    met: (b, _s, status) =>
      status === "move_out_pending" && nonEmpty(b?.move_out_date),
  },
  {
    id: "key_return_pending",
    requirement: "Move-out protocol submitted",
    met: (_b, s) => s.moveOutProtocolSubmitted,
  },
  {
    id: "deposit_refund_pending",
    // The gate that used to complete `moved_out`. Keys being back ends the
    // tenant's occupation, not the tenancy: the deposit is still held.
    requirement: "Key return recorded",
    met: (b) => !!b?.key_returned_at,
  },
  {
    id: "moved_out",
    // Both halves. A released deposit the tenant has not actually been paid is
    // not a finished tenancy, and "we decided" is not "you have your money".
    //
    // A tenancy with no deposit held has nothing to settle and would otherwise
    // sit in a stage it could never leave.
    requirement: "Deposit refund settled",
    met: (_b, s) => !!s.noDepositHeld || !!s.depositRefundSettled,
  },
] as const;

/** Truthy and not an empty/whitespace string — DB text columns arrive as "". */
const nonEmpty = (v: unknown): boolean =>
  v != null && v !== false && String(v).trim().length > 0;

/**
 * Is there an actual contract DOCUMENT on file?
 *
 * `bookings.status` and `bookings.contract_status` are free-standing enum
 * columns that any flow (or an admin) can set — they are a CLAIM that a
 * contract exists, never evidence of one. This checks for the artefact itself:
 * a stored PDF (generated draft, legacy `contract_url`, or an uploaded signed
 * copy), or a DocuSign envelope, which is where the document lives when it
 * hasn't been mirrored into our storage yet.
 *
 * Every contract gate is anded with this, so a tenant can no longer climb the
 * ladder — and read "Active" — with `contract_status = 'signed'` and nothing
 * on file.
 */
export function hasContractDocument(booking: any): boolean {
  if (!booking) return false;
  return (
    nonEmpty(booking.contract_pdf_url) ||
    nonEmpty(booking.contract_url) ||
    nonEmpty(booking.signed_contract_url) ||
    nonEmpty(booking.docusign_envelope_id)
  );
}

/**
 * A contract is considered "sent for signature" once a document exists AND it
 * has left the not-ready / draft state — i.e. a DocuSign envelope exists, the
 * tenant can sign, or it's already been signed/approved. Mirrors the pre-sales
 * half of leaseStatus.ts, plus the document requirement above.
 */
export function isContractSentForSignature(booking: any): boolean {
  if (!booking) return false;
  // GOLDEN RULE: no document, no contract stage — whatever the status says.
  if (!hasContractDocument(booking)) return false;
  // A generated-but-unsent DRAFT is not "sent". The admin contract area builds
  // the PDF first and releases it in a separate, confirmed step, so a stored
  // PDF alone no longer implies the tenant ever saw it — `contract_status =
  // 'draft'` is the marker for "on file, deliberately not released". Anything
  // downstream of an actual signature (an envelope, a signed copy, a signing
  // timestamp) still wins, so this can't strand a tenant who did sign.
  if (
    String(booking.contract_status ?? "").toLowerCase() === "draft" &&
    !booking.docusign_envelope_id &&
    !booking.docusign_status &&
    !booking.signed_contract_url &&
    !booking.contract_signed_at
  ) {
    return false;
  }
  if (booking.status === "signature_pending") return true;
  if (booking.docusign_envelope_id || booking.docusign_status) return true;
  if (booking.signed_contract_url || booking.contract_pdf_url) return true;
  const cs = String(booking.contract_status ?? "").toLowerCase();
  return (
    cs === "ready_to_sign" ||
    cs === "awaiting_approval" ||
    cs === "envelope_draft" ||
    cs === "signed" ||
    cs === "approved"
  );
}

/**
 * "Contract signed" for journey purposes — deliberately stricter than
 * `isBookingContractSignedIncludingManual` (which analytics/occupancy use and
 * must stay permissive so manually-handled tenants aren't undercounted).
 *
 * Here a signature needs BOTH a document on file and a signature signal:
 *  - electronic / DocuSign → `contract_signed_at`, or contract_status
 *    signed/approved, against a stored PDF or a DocuSign envelope;
 *  - manual handling → the signed PDF itself. The `manual_contract_upload`
 *    flag on its own is a claim that an upload happened; without
 *    `signed_contract_url` there is nothing to show for it.
 */
export function isContractSignedForJourney(booking: any): boolean {
  if (!booking) return false;
  if (!hasContractDocument(booking)) return false;
  if (isBookingContractSigned(booking)) return true;
  return nonEmpty(booking.signed_contract_url);
}

/**
 * Resolve the current journey stage from real booking data.
 *
 * Sequential (the golden rule): walk the gate ladder from Applied upward and
 * stop at the first stage whose entry condition is NOT met. A later signal never
 * leapfrogs a missing earlier one — a booking marked `active` with an unsigned
 * contract resolves to Signature pending (with `statusAhead` set), not Active.
 *
 * The single exception is a terminal status (moved_out / terminated /
 * cancelled). Those are exits — a deal can be cancelled at any point — so they
 * always resolve to the closed stage rather than climbing the ladder.
 */
export function resolveTenantJourney(
  booking: any,
  signals: TenantJourneySignals
): TenantJourney {
  const status = String(booking?.status ?? "").toLowerCase();

  // Sequential walk: advance while the next gate's condition holds.
  let stageIndex = 0;
  let blockedGate: JourneyGate | null = null;
  for (let i = 1; i < JOURNEY_GATES.length; i++) {
    const gate = JOURNEY_GATES[i];
    if (!gate.met(booking, signals, status)) {
      blockedGate = gate;
      break;
    }
    stageIndex = i;
  }

  // Terminal statuses are exits, not upgrades — they always win.
  const closed = CLOSED_STATUSES.has(status);
  if (closed) {
    stageIndex = STAGE_INDEX.moved_out;
    blockedGate = null;
  }

  const stageId = TENANT_JOURNEY_STAGES[stageIndex].id;
  const claimedStageId = closed ? null : (STATUS_CLAIMED_STAGE[status] ?? null);
  const terminalStatus: TenantJourney["terminalStatus"] =
    status === "terminated"
      ? "terminated"
      : status === "cancelled"
        ? "cancelled"
        : stageId === "moved_out"
          ? "moved_out"
          : null;
  return {
    stageId,
    stageIndex,
    stage: TENANT_JOURNEY_STAGES[stageIndex],
    stepText: `Step ${stageIndex + 1} of ${TENANT_JOURNEY_STAGES.length}`,
    isClosed: closed || stageId === "moved_out",
    terminalStatus,
    reached: (id) => stageIndex >= STAGE_INDEX[id],
    bookingStatus: status,
    blockedBy: blockedGate
      ? { stageId: blockedGate.id, requirement: blockedGate.requirement }
      : null,
    claimedStageId,
    statusAhead:
      claimedStageId != null && STAGE_INDEX[claimedStageId] > stageIndex,
  };
}

/* ================================================================== */
/* Force-stage specs (super-admin manual override)                     */
/* ================================================================== */

/**
 * The exact signal state that makes `resolveTenantJourney` land on a given
 * stage. Used by the tenant profile's "force stage" override: it writes booking
 * columns (`bookingColumnsForStage`) plus the derived deposit / protocol records
 * so the resolver resolves to precisely the picked stage.
 *
 * `contractSigned` describes the signed predicate
 * (`isBookingContractSignedIncludingManual`); any stage that must NOT read as
 * signed clears the signed artifacts (`signed_contract_url` /
 * `manual_contract_upload`), because those count as signed regardless of
 * `contract_status`. Reaching a stage BELOW "signature pending" (i.e. Applied)
 * additionally clears the SENT artifacts (`contract_pdf_url` / docusign fields),
 * which flip `isContractSentForSignature` true on their own — otherwise a forced
 * "Applied" would snap back to Signature pending on any booking that ever had a
 * real contract.
 */
export interface StageForceSpec {
  /** raw bookings.status to write */
  status: string;
  /** raw bookings.contract_status to write */
  contractStatus: string;
  /** whether the contract should read as signed (sets/clears contract_signed_at) */
  contractSigned: boolean;
  /** clear docusign/url contract artifacts so the stage can drop below "sent" */
  clearContractArtifacts: boolean;
  /** whether the deposit should read as paid */
  depositPaid: boolean;
  /** whether a key pickup timestamp should be set */
  keyPickedUp: boolean;
  /** whether a submitted move-in protocol is allowed to remain (pre-Active: no) */
  allowMoveInProto: boolean;
  /** whether a key return timestamp should be set */
  keyReturned: boolean;
  /** whether a submitted move-out protocol must exist */
  moveOutProtoSubmitted: boolean;
  /**
   * whether `move_out_date` must be set — the "Move-out date confirmed" gate
   * needs the date itself, not just `status = move_out_pending`.
   */
  moveOutDateSet: boolean;
  /** whether `move_in_confirmed_at` must be set (the Active gate's evidence) */
  moveInConfirmed: boolean;
  /** whether `move_out_notice_at` must be set (Move-out scheduled's evidence) */
  moveOutNoticed: boolean;
}

export const STAGE_FORCE_SPECS: Record<TenantJourneyStageId, StageForceSpec> = {
  applied: {
    status: "pending",
    contractStatus: "not_ready",
    contractSigned: false,
    clearContractArtifacts: true,
    depositPaid: false,
    keyPickedUp: false,
    allowMoveInProto: false,
    keyReturned: false,
    moveInConfirmed: false,
    moveOutNoticed: false,
    moveOutDateSet: false,
    moveOutProtoSubmitted: false,
  },
  signature_pending: {
    status: "signature_pending",
    contractStatus: "ready_to_sign",
    contractSigned: false,
    clearContractArtifacts: false,
    depositPaid: false,
    keyPickedUp: false,
    allowMoveInProto: false,
    keyReturned: false,
    moveInConfirmed: false,
    moveOutNoticed: false,
    moveOutDateSet: false,
    moveOutProtoSubmitted: false,
  },
  deposit_pending: {
    status: "confirmed",
    contractStatus: "signed",
    contractSigned: true,
    clearContractArtifacts: false,
    depositPaid: false,
    keyPickedUp: false,
    allowMoveInProto: false,
    keyReturned: false,
    moveInConfirmed: false,
    moveOutNoticed: false,
    moveOutDateSet: false,
    moveOutProtoSubmitted: false,
  },
  key_pickup_pending: {
    status: "confirmed",
    contractStatus: "signed",
    contractSigned: true,
    clearContractArtifacts: false,
    depositPaid: true,
    keyPickedUp: false,
    allowMoveInProto: false,
    keyReturned: false,
    moveInConfirmed: false,
    moveOutNoticed: false,
    moveOutDateSet: false,
    moveOutProtoSubmitted: false,
  },
  handover_pending: {
    status: "confirmed",
    contractStatus: "signed",
    contractSigned: true,
    clearContractArtifacts: false,
    depositPaid: true,
    keyPickedUp: true,
    allowMoveInProto: false,
    keyReturned: false,
    moveInConfirmed: false,
    moveOutNoticed: false,
    moveOutDateSet: false,
    moveOutProtoSubmitted: false,
  },
  active: {
    status: "active",
    contractStatus: "signed",
    contractSigned: true,
    clearContractArtifacts: false,
    depositPaid: true,
    keyPickedUp: true,
    allowMoveInProto: true,
    keyReturned: false,
    moveInConfirmed: true,
    moveOutNoticed: false,
    moveOutDateSet: false,
    moveOutProtoSubmitted: false,
  },
  moveout_scheduled: {
    status: "notice_given",
    contractStatus: "signed",
    contractSigned: true,
    clearContractArtifacts: false,
    depositPaid: true,
    keyPickedUp: true,
    allowMoveInProto: true,
    keyReturned: false,
    moveInConfirmed: true,
    moveOutNoticed: true,
    moveOutDateSet: false,
    moveOutProtoSubmitted: false,
  },
  moveout_protocol_pending: {
    status: "move_out_pending",
    contractStatus: "signed",
    contractSigned: true,
    clearContractArtifacts: false,
    depositPaid: true,
    keyPickedUp: true,
    allowMoveInProto: true,
    keyReturned: false,
    moveInConfirmed: true,
    moveOutNoticed: true,
    moveOutDateSet: true,
    moveOutProtoSubmitted: false,
  },
  key_return_pending: {
    status: "move_out_pending",
    contractStatus: "signed",
    contractSigned: true,
    clearContractArtifacts: false,
    depositPaid: true,
    keyPickedUp: true,
    allowMoveInProto: true,
    keyReturned: false,
    moveInConfirmed: true,
    moveOutNoticed: true,
    moveOutDateSet: true,
    moveOutProtoSubmitted: true,
  },
  deposit_refund_pending: {
    // Keys are back but the deposit is unsettled. The status stays
    // move_out_pending on purpose — forcing `moved_out` would trip the terminal
    // short-circuit in resolveTenantJourney and land on the wrong stage.
    status: "move_out_pending",
    contractStatus: "signed",
    contractSigned: true,
    clearContractArtifacts: false,
    depositPaid: true,
    keyPickedUp: true,
    allowMoveInProto: true,
    keyReturned: true,
    moveInConfirmed: true,
    moveOutNoticed: true,
    moveOutDateSet: true,
    moveOutProtoSubmitted: true,
  },
  moved_out: {
    status: "moved_out",
    contractStatus: "signed",
    contractSigned: true,
    clearContractArtifacts: false,
    depositPaid: true,
    keyPickedUp: true,
    allowMoveInProto: true,
    keyReturned: true,
    moveInConfirmed: true,
    moveOutNoticed: true,
    moveOutDateSet: true,
    moveOutProtoSubmitted: true,
  },
};

/**
 * Booking-column patch that forces `target`. `nowIso` supplies timestamps
 * (passed in so this stays pure/testable); existing timestamps are preserved.
 *
 * PRECONDITION for any target at or past `signature_pending`: the booking must
 * already have a contract document (`hasContractDocument`). The patch cannot
 * manufacture one — a contract is a real artefact, not a column — so a forced
 * status without it would resolve straight back to Applied. Callers check this
 * up front and refuse the jump with an actionable message.
 */
export function bookingColumnsForStage(
  target: TenantJourneyStageId,
  booking: any,
  nowIso: string
): Record<string, any> {
  const t = STAGE_FORCE_SPECS[target];
  const patch: Record<string, any> = {
    status: t.status,
    contract_status: t.contractStatus,
    contract_signed_at: t.contractSigned
      ? (booking?.contract_signed_at ?? nowIso)
      : null,
    key_picked_up_at: t.keyPickedUp
      ? (booking?.key_picked_up_at ?? nowIso)
      : null,
    key_returned_at: t.keyReturned
      ? (booking?.key_returned_at ?? nowIso)
      : null,
  };
  // Any stage below "contract signed" must not carry signed artifacts — an
  // uploaded signed PDF / the manual-upload flag read as signed on their own.
  if (!t.contractSigned) {
    patch.signed_contract_url = null;
    patch.manual_contract_upload = false;
  }
  if (t.clearContractArtifacts) {
    patch.contract_pdf_url = null;
    patch.docusign_envelope_id = null;
    patch.docusign_status = null;
  }
  // "Move-out date confirmed" is gated on the date itself, so a forced stage at
  // or past it has to carry one — reuse whatever is already on the booking
  // (confirmed date, then lease end) before falling back to today.
  if (t.moveOutDateSet) {
    patch.move_out_date =
      booking?.move_out_date ?? booking?.end_date ?? nowIso.slice(0, 10);
  }
  // Active and the move-out stages are gated on their own timestamps now, so
  // the force patch writes them (preserving any real value) — and clears them
  // for stages below, or a forced downgrade would snap straight back up.
  //
  // `actual_move_in_date` is deliberately NOT touched. It is the Active gate's
  // backfill source, not one of its live inputs, and it also feeds the
  // Wohnungsgeberbestätigung send flow (idx_bookings_woh_pending_send) — a
  // stage correction must not erase another feature's data.
  patch.move_in_confirmed_at = t.moveInConfirmed
    ? (booking?.move_in_confirmed_at ?? nowIso)
    : null;
  patch.move_out_notice_at = t.moveOutNoticed
    ? (booking?.move_out_notice_at ?? nowIso)
    : null;
  return patch;
}

/**
 * The resolver signals that a forced `target` guarantees. A move-in protocol is
 * never fabricated — Active is reached via the `move_in_confirmed_at` column
 * that `bookingColumnsForStage` writes — so the guaranteed value is always
 * false; deposit + move-out protocol follow the spec.
 */
export function signalsForStage(
  target: TenantJourneyStageId
): TenantJourneySignals {
  const t = STAGE_FORCE_SPECS[target];
  return {
    depositPaid: t.depositPaid,
    moveInProtocolSubmitted: false,
    moveOutProtocolSubmitted: t.moveOutProtoSubmitted,
  };
}

/**
 * Can the deposit still be edited at this stage? (COL-360)
 *
 * Once the deposit stage is behind the tenant — keys are being handed over —
 * the deposit has been settled, and editing it (or discounting it) would put
 * `bookings.deposit_amount` out of step with money already received. The
 * expected-deposit comparison in `sync_room_booking_after_deposit_and_rent_paid`
 * is what would break: a tenant who paid in full can be shown as short forever.
 *
 * Rent stays editable throughout — only the deposit locks.
 */
export function isDepositEditLocked(stage: TenantJourneyStageId): boolean {
  const order = TENANT_JOURNEY_STAGES.findIndex((s) => s.id === stage);
  const lockFrom = TENANT_JOURNEY_STAGES.findIndex(
    (s) => s.id === "key_pickup_pending"
  );
  if (order < 0 || lockFrom < 0) return false;
  return order >= lockFrom;
}
